import * as React from "react";
import * as Sentry from "@sentry/browser"; // error tacking
import { AgGridReact } from "ag-grid-react";
import cloneDeep from "lodash/cloneDeep";
import { merge, $ } from "glamor";
import { GridApi, GridReadyEvent, CellValueChangedEvent, ColDef } from "ag-grid-community";
import * as AgGridEnterprise from "ag-grid-enterprise";
import "ag-grid-community/dist/styles/ag-grid.css";
import "ag-grid-community/dist/styles/ag-theme-balham.css";
import { formatMessage } from "lib/globalize-util";
import {
    IDeliveryPurchaseOrder,
    PurchaseOrderFields,
    DeliveryPurchaseOrderFields,
    IPurchaseOrderDoorGroup,
    getDeliveryPurchaseOrderFieldLabel,
    purchaseOrderFieldsConstraints,
} from "helpers/delivery-purchase-order";

import { NumberCellEditor } from "components/product-collection/product-collection-views/product-collection-grid-view/grid-editors/number-cell-editor";
import { DateCellEditor } from "components/product-collection/product-collection-views/product-collection-grid-view/grid-editors/date-cell-editor";
import { TextEditor } from "components/product-collection/product-collection-views/product-collection-grid-view/grid-editors/text-editor";
import { dateNumberValueFormatter } from "components/product-collection/product-collection-views/product-collection-grid-view/grid-formatters/date-number";
import { DefaultRendererVanilla } from "components/product-collection/product-collection-views/product-collection-grid-view/grid-renderers/default-renderer-vanilla";
import { IAllocationDimension } from "helpers/allocation-dimension";
import { DATATEST } from "constants/integration";
import { theme } from "styles/theme";

const GRID_COMPONENTS = {
    numberCellEditor: NumberCellEditor,
    textEditor: TextEditor,
    dateCellEditor: DateCellEditor,
};

const GRID_COMPONENTS_VANILLA = {
    defaultRenderer: DefaultRendererVanilla,
};

const getRowNodeId = (data: IDeliveryPurchaseOrder) => {
    return data.id;
};

const HEADER_HEIGHT = 77;
const ROW_HEIGHT = 28;

const styles = merge(
    {
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
    },
    $(" .purchaseOrdersGrid", {
        width: "100%",
        margin: "0",
        flexGrow: 1,
    }),
    $(" .purchaseOrdersGrid .ag-root-wrapper-body", {
        height: "auto",
    }),
    $(" .ag-status-bar", {
        display: "none",
    }),
    $(" .noValueDesired", {
        backgroundColor: "#FFD300",
    })
);

const getContextMenuItems = () => ["copy", "separator", "export"];

const commonColDefProps = {
    lockPosition: true,
    suppressMovable: true,
    floatingFilter: true,
    suppressMenu: true,
    resizable: true,
    filter: true,
    sortable: true,
};

interface IProps {
    parentAllocationDimension?: IAllocationDimension;
    readOnly?: boolean;
    onRowUpdated?: (rowData: IDeliveryPurchaseOrder, index: number) => void;
    onRowError?: (errorMessage: string) => void;
    onCellEditing?: (editMode: boolean) => void;
    hiddenColumns?: string[];
    uneditableColumns?: string[];
    deliveryPurchaseOrders: IDeliveryPurchaseOrder[];
    purchaseOrderDoorGroups: IPurchaseOrderDoorGroup[];
    validatePOTable: boolean;
}
export const DeliveryPurchaseOrdersGrid: React.FunctionComponent<IProps> = ({
    parentAllocationDimension,
    readOnly,
    onRowUpdated,
    onCellEditing,
    hiddenColumns = [],
    uneditableColumns = [],
    deliveryPurchaseOrders,
    purchaseOrderDoorGroups,
    validatePOTable,
}) => {
    const cellClassRules = React.useMemo(
        () => ({
            noValueDesired: (data) => {
                if (!validatePOTable) {
                    return false;
                }

                const desiredField = purchaseOrderFieldsConstraints.some(
                    (POConstraintField) =>
                        POConstraintField.fieldName === data.colDef.field && POConstraintField.desired
                );

                if (desiredField) {
                    return !data.value;
                }

                return false;
            },
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [hiddenColumns, parentAllocationDimension, purchaseOrderDoorGroups, validatePOTable]
    );

    const columnDefs = React.useMemo<ColDef[]>(() => {
        const colDefs: ColDef[] = [
            {
                field: DeliveryPurchaseOrderFields.PurchaseOrderDoorGroupId,
                headerName: getDeliveryPurchaseOrderFieldLabel(DeliveryPurchaseOrderFields.PurchaseOrderDoorGroupId),
                editable: false,
                valueFormatter: ({ value }) =>
                    purchaseOrderDoorGroups.find((purchaseOrderDoorGroup) => purchaseOrderDoorGroup.id === value)?.name,
            },
            {
                field: PurchaseOrderFields.PoNumber,
                headerName: getDeliveryPurchaseOrderFieldLabel(PurchaseOrderFields.PoNumber),
                editable: !readOnly && !uneditableColumns.includes(PurchaseOrderFields.PoNumber),
                cellClassRules,
            },
            {
                field: PurchaseOrderFields.ShipDate,
                headerName: getDeliveryPurchaseOrderFieldLabel(PurchaseOrderFields.ShipDate),
                editable: !readOnly && !uneditableColumns.includes(PurchaseOrderFields.ShipDate),
                cellEditor: "dateCellEditor",
                cellEditorParams: {
                    maskFormat: "MM/DD/YYYY",
                },
                valueFormatter: dateNumberValueFormatter,
                cellClassRules,
            },
            {
                field: PurchaseOrderFields.CancelDate,
                headerName: getDeliveryPurchaseOrderFieldLabel(PurchaseOrderFields.CancelDate),
                editable: !readOnly && !uneditableColumns.includes(PurchaseOrderFields.CancelDate),
                cellEditor: "dateCellEditor",
                cellEditorParams: {
                    maskFormat: "MM/DD/YYYY",
                },
                valueFormatter: dateNumberValueFormatter,
                cellClassRules,
            },
        ];

        if (parentAllocationDimension) {
            colDefs.unshift({
                headerName: parentAllocationDimension.name,
                field: DeliveryPurchaseOrderFields.DeliveryId,
                hide: true,
                rowGroup: true,
                editable: false,
                valueFormatter: ({ value }) => {
                    return parentAllocationDimension.dimension_values.find(
                        (dimensionValue) => dimensionValue.id === value
                    )?.name;
                },
            });
        }

        const filteredColDefs = colDefs.filter((colDef) => !hiddenColumns.includes(colDef.field));

        return filteredColDefs;
    }, [hiddenColumns, parentAllocationDimension, purchaseOrderDoorGroups]); // eslint-disable-line react-hooks/exhaustive-deps

    // Only show purchase orders that are active
    const filterDeliveryPurchasedOrders = React.useCallback((deliveryPurchaseOrders: IDeliveryPurchaseOrder[]) => {
        return deliveryPurchaseOrders.filter(
            (deliveryPurchaseOrder) => deliveryPurchaseOrder.id && deliveryPurchaseOrder.active
        );
    }, []);

    const handleCellChange = React.useCallback((e: CellValueChangedEvent) => {
        const { oldValue, newValue, data, api, colDef } = e;
        if (oldValue === newValue) {
            return;
        }

        if (onRowUpdated) {
            const index = deliveryPurchaseOrders.findIndex(
                (deliveryPurchaseOrder) => deliveryPurchaseOrder.id === data.id
            );
            if (index >= 0) {
                onRowUpdated(data, index);
            }
        }
        const filterInstance = api.getFilterInstance(colDef.field);
        (filterInstance as AgGridEnterprise.SetFilter<string>)?.resetFilterValues();
        api.onFilterChanged();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const disableDuplicatePOs = React.useCallback(
        (deliveryPurchaseOrders) => {
            // Force duplicate POs to inactive
            const duplicatePOs: { item: IDeliveryPurchaseOrder; index: number }[] = [];
            const duplicateMap = {};
            deliveryPurchaseOrders.forEach((deliveryPurchaseOrder, index) => {
                const key = `${deliveryPurchaseOrder.delivery_id}_${deliveryPurchaseOrder.purchase_order_door_group_id}`;

                if (duplicateMap[key]) {
                    duplicatePOs.push({
                        item: deliveryPurchaseOrder,
                        index,
                    });
                } else {
                    duplicateMap[key] = true;
                }
            });

            duplicatePOs.forEach((duplicatePO) => {
                if (duplicatePO.item.active) {
                    onRowUpdated &&
                        onRowUpdated(
                            {
                                ...duplicatePO.item,
                                active: false,
                            },
                            duplicatePO.index
                        );

                    Sentry.captureException(new Error("Duplicate delivery purchase order"));
                    // eslint-disable-next-line no-console
                    console.warn("Duplicate delivery purchase order", duplicatePO.item, duplicatePO.index);
                }
            });
        },
        [onRowUpdated]
    );

    const [gridApi, setGridApi] = React.useState<GridApi | null>(null);
    const handleOnGridReady = (e: GridReadyEvent) => {
        setGridApi(e.api);

        e.api.forEachNode((node) => {
            // Expand row group by default
            if (node.field === DeliveryPurchaseOrderFields.DeliveryId) {
                node.setExpanded(true);
            }
        });
    };

    const { rowData } = React.useMemo(() => {
        // Ensure immutability so mutating row data won't affect anything outside ag-grid
        const rows = cloneDeep(filterDeliveryPurchasedOrders(deliveryPurchaseOrders));

        return { rowData: rows };
    }, [deliveryPurchaseOrders]); // eslint-disable-line react-hooks/exhaustive-deps

    React.useEffect(() => {
        if (!gridApi) {
            return;
        }
        gridApi.setRowData(rowData);
    }, [rowData]); // eslint-disable-line react-hooks/exhaustive-deps

    React.useEffect(() => {
        disableDuplicatePOs(deliveryPurchaseOrders);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleCellEditingStarted = React.useCallback(() => {
        if (onCellEditing) {
            onCellEditing(true);
        }
    }, [onCellEditing]);

    const rows = rowData.length + 1; // pinned row
    const containerHeight = HEADER_HEIGHT + rows * ROW_HEIGHT + 2; /* borders */
    return (
        <div {...styles}>
            <div
                className={`${theme.agGrid.defaultTheme} purchaseOrdersGrid`}
                style={{ height: `${containerHeight}px` }}
                data-test={DATATEST.ASSORTMENTS.DELIVERY_PURCHASE_ORDERS.GRID_WRAPPER}
            >
                <AgGridReact
                    onGridReady={handleOnGridReady}
                    defaultColDef={commonColDefProps}
                    immutableData={true}
                    columnDefs={columnDefs}
                    rowData={rowData}
                    rowDragManaged={false}
                    debug={false}
                    components={GRID_COMPONENTS_VANILLA}
                    frameworkComponents={GRID_COMPONENTS}
                    enableRangeSelection={true}
                    rowSelection="multiple"
                    enableCellChangeFlash={true}
                    animateRows={false}
                    suppressCopyRowsToClipboard={true}
                    stopEditingWhenCellsLoseFocus={true}
                    onCellValueChanged={handleCellChange}
                    getRowNodeId={getRowNodeId}
                    getContextMenuItems={getContextMenuItems}
                    onCellEditingStarted={handleCellEditingStarted}
                    suppressRowClickSelection={true} // if true, clicking rows doesn't select (useful for checkbox selection)
                    overlayNoRowsTemplate={`<span style="position: relative; top: 32px;">${formatMessage(
                        "Please assign purchase orders for this assortment in the Assign Door Labels modal."
                    )}</span>`}
                    rowBuffer={50}
                />
            </div>
        </div>
    );
};
